<script setup lang="ts">
/**
 * Фильтр-чип панели — макет 34:270: подпись, шеврон, под ними список с
 * галочками. Выбор множественный: «покажи баги и задачи» — один вопрос, а не
 * два подряд.
 *
 * Своя панель, а не `AppPopover` из пакета: тот стоит на floating-vue, которого
 * в зависимостях трекера нет, а чипу под панелью хватает `onClickOutside` —
 * список открывается ровно под своим чипом и никуда не всплывает.
 */
import { onClickOutside, useEventListener } from '@vueuse/core'
import { Check, ChevronDown } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  label: string
  options: { value: string; label: string }[]
  modelValue: string[]
  /** Что написать вместо списка, когда выбирать не из чего */
  emptyText: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: string[]] }>()

const { t } = useI18n()

const root = ref<HTMLElement | null>(null)
const isOpen = ref(false)

onClickOutside(root, () => {
  isOpen.value = false
})

// Escape ловим на документе, а не на панели: клавиатура в этот момент чаще
// всего не в ней — открыли список, передумали и жмут Escape, глядя на доску.
useEventListener(document, 'keydown', (event: KeyboardEvent) => {
  if (event.key === 'Escape') isOpen.value = false
})

/**
 * Выбран один — на чипе стоит его имя: «Тип: Баг» отвечает на вопрос, не
 * заставляя открывать список. Больше одного — счётчик: имена не влезают.
 */
const caption = computed(() => {
  if (!props.modelValue.length) return props.label

  const [first] = props.modelValue

  if (props.modelValue.length === 1) {
    const option = props.options.find((entry) => entry.value === first)

    return option ? `${props.label}: ${option.label}` : props.label
  }

  return `${props.label}: ${props.modelValue.length}`
})

const toggle = (value: string) => {
  emit(
    'update:modelValue',
    props.modelValue.includes(value)
      ? props.modelValue.filter((entry) => entry !== value)
      : [...props.modelValue, value],
  )
}
</script>

<template>
  <div ref="root" class="fchip">
    <button
      type="button"
      class="fchip__button"
      :class="{ 'fchip__button--active': modelValue.length > 0 }"
      :aria-expanded="isOpen"
      aria-haspopup="listbox"
      @click="isOpen = !isOpen"
    >
      <span class="fchip__caption">{{ caption }}</span>
      <ChevronDown :size="14" />
    </button>

    <div v-if="isOpen" class="fchip__panel" role="listbox">
      <button
        v-if="modelValue.length"
        type="button"
        class="fchip__reset"
        @click="emit('update:modelValue', [])"
      >
        {{ t('toolbar.filters.reset') }}
      </button>

      <p v-if="!options.length" class="fchip__empty">{{ emptyText }}</p>

      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="fchip__option"
        role="option"
        :aria-selected="modelValue.includes(option.value)"
        @click="toggle(option.value)"
      >
        <Check v-if="modelValue.includes(option.value)" class="fchip__check" :size="14" />
        <span v-else class="fchip__check" aria-hidden="true" />
        {{ option.label }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.fchip {
  position: relative;
  flex-shrink: 0;
}

.fchip__button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  cursor: pointer;
}

.fchip__button:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

/* Отбор виден и не открывая список: иначе доска молча показывает половину
   задач, а человек ищет пропавшую карточку в чужой колонке. */
.fchip__button--active {
  border-color: var(--color-primary);
  background: var(--color-primary-tint);
  color: var(--color-primary);
}

.fchip__caption {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fchip__panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-height: 320px;
  padding: 4px;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.fchip__option,
.fchip__reset {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.fchip__option:hover,
.fchip__reset:hover {
  background: var(--color-bg-muted);
}

.fchip__reset {
  color: var(--color-text-muted);
}

/* Галочка держит своё место и когда её нет: иначе строки прыгают при выборе. */
.fchip__check {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  color: var(--color-primary);
}

.fchip__empty {
  margin: 0;
  padding: 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
  white-space: nowrap;
}
</style>
